"use client";

import { useEffect } from "react";
import { IconBolt, IconHeart } from "@tabler/icons-react";
import { useMediaQuery } from "react-responsive";
import CouponIcon from "@/icons/Coupon";
import AuctionIcon from "@/icons/Auction";
import NewWindowIcon from "@/icons/NewWindow";
import CheckBox from "@/components/Dropdown/checkbox";
import Tag from "@/components/FilterMenu/tag";
import styles from "./styles.module.scss";
import {
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";

const PageContent = () => {
  const isMobile = useMediaQuery({ query: "(max-width: 850px)" });

  useEffect(() => {
    const htmlBody = document.querySelector("body");
    if (!htmlBody) return;
    htmlBody.style.overflowY = "hidden";

    const navHeight = document.querySelector("nav")?.offsetHeight;
    const contentSection = document.querySelector(".contentSection") as HTMLElement;

    if (!contentSection) return;

    if (isMobile) {
      contentSection.style.height = `100%`;
      return;
    }

    contentSection.style.height = `calc(100vh - ${navHeight}px)`;

    // Cleanup on unmount
    return () => {
      htmlBody.style.overflowY = "auto";
      contentSection.style.height = "unset";
    };
  }, [isMobile]);

  return (
    <MantineBox className={styles.infoPage}>
      <MantineTitle order={1} className={styles.mainTitle}>
        About KURB
      </MantineTitle>
      <MantineBox
        className={`${isMobile ? "w-full" : "w-1/2"} h-full p-2 text-justify ${!isMobile && "overflow-y-scroll"}`}
      >
        <MantineBox className={styles.introRow}>
          <MantineBox className={styles.introText}>
            <MantineText>
              KURB is a platform founded by fashion obsessives for fashion obsessives, with one
              objective in mind: to illuminate and advance the potential of the fashion resale
              market. Designer resale carries immense opportunity; there is enough valuable designer
              clothing to tide ourselves over for generations to come, but it&apos;s impeded by
              fragmentation, making it difficult to navigate and obfuscating its value, leading to
              people overlook its banquet of garments. KURB is the conduit that connects the resale
              landscape, bridging its terrain and widening its horizon. By doing so it will
              demonstrate that an alternative way of doing fashion is possible.
            </MantineText>
            <br />
            <MantineText>
              KURB operates on the principle that the only constant is change, and recognizes that
              the universe of online designer clothes is in continuous flux. New vendors appear
              spontaneously, eager to find their audience. Often these vendors are founded purely
              out of passion for a certain aesthetic or period in fashion history, offering a
              respite from the oversaturation of the mainstream. These are the places where
              creativity and authenticity thrive, and they deserve to be seen by more people.
              Through KURB, their visibility and prowess in fashion is extended, while giving users
              a portal to an ever growing variety of rich sartorial worlds. KURB endeavours to grow
              with the community from which it originates, with the aim to give back in mind.
            </MantineText>
          </MantineBox>
          <MantineBox className={styles.qrBlock}>
            <MantineText>
              <MantineText span fw={700}>
                SHARE
              </MantineText>
            </MantineText>
            <MantineImage src="/qr.svg" alt="QR code" style={{ marginTop: "4px" }} />
          </MantineBox>
        </MantineBox>
        <br />
        <MantineText>
          Here you can read more about{" "}
          <MantineAnchor href="/blog/kurb-genesis">where KURB came from</MantineAnchor> and{" "}
          <MantineAnchor href="/blog/why-we-exist">what KURB is here</MantineAnchor>.
        </MantineText>
      </MantineBox>
      <MantineBox className={styles.exapandableSectionContainer}>
        <MantineText span fw={700} style={{ fontSize: "1.2rem" }}>
          KURBO mode
        </MantineText>
        <MantineText>
          KURBO is the real-time search layer built into KURB. Enable it on the search page to
          surface listings added in the last hour that match your active filters, the freshest items
          on the market, before anyone else finds them. Each KURBO search draws one token from your
          daily budget.
        </MantineText>
        <MantineText span fw={700} style={{ fontSize: "1.2rem" }}>
          Favorites
        </MantineText>
        <MantineText>
          KURB&apos;s bots are always active and adding new content every minute. If you find
          something you like, we recommend adding it to your favorite items to easily find it later.
        </MantineText>
        <MantineBox className={styles.legend}>
          <MantineText span fw={700}>
            Legend
          </MantineText>
          <MantineBox className={styles.legendItem}>
            <MantineText span className={styles.legendIcon} aria-hidden="true">
              <IconBolt size={22} stroke={1.75} />
            </MantineText>
            <MantineText component="span">
              - Your daily token budget for KURBO mode and price drop monitors. Resets at midnight.
              Upgrade for a larger budget.
            </MantineText>
          </MantineBox>
          <MantineBox className={styles.legendItem}>
            <MantineText span className={styles.legendIcon} aria-hidden="true">
              <IconHeart size={22} stroke={1.75} />
            </MantineText>
            <MantineText component="span">
              - Save items to your favorites to track them and set price drop alerts.
            </MantineText>
          </MantineBox>
          <MantineBox className={styles.legendItem}>
            <MantineText
              span
              className={`${styles.legendIcon} ${styles.auctionIcon}`}
              aria-hidden="true"
            >
              <AuctionIcon width={26} height={26} />
            </MantineText>
            <MantineText component="span">- The item is sold via auction</MantineText>
          </MantineBox>
          <MantineBox className={styles.legendItem}>
            <MantineText span className={styles.legendIcon} aria-hidden="true">
              <CouponIcon />
            </MantineText>
            <MantineText component="span">- A coupon code is available for this item</MantineText>
          </MantineBox>
          <MantineBox className={styles.legendItem}>
            <MantineText span className={styles.legendIcon} aria-hidden="true">
              <NewWindowIcon />
            </MantineText>
            <MantineText component="span">- Navigate to the item page</MantineText>
          </MantineBox>
          <MantineText span fw={700}>
            Filtering
          </MantineText>
          <MantineText>
            The filtering system offers precise control with a two-state checkbox. Click once to
            include an option, and twice to exclude it.
          </MantineText>
          <CheckBox
            selected={{ id: 1, value: "include", label: "Include", state: "include" }}
            option={{ id: 1, value: "include", label: "Include" }}
          />
          <CheckBox
            selected={{ id: 2, value: "exclude", label: "Exclude", state: "exclude" }}
            option={{ id: 2, value: "exclude", label: "Exclude", state: "exclude" }}
          />
          <CheckBox option={{ id: 3, value: "not-selected", label: "Not selected" }} />
          <MantineBox className={styles.tagContainer}>
            <Tag
              onClick={() => {}}
              item={{
                option: {
                  id: 4,
                  label: "Included",
                  value: "included",
                  state: "include",
                },
                dropdown: "brands",
              }}
            />
            <Tag
              onClick={() => {}}
              item={{
                option: {
                  id: 5,
                  label: "Excluded",
                  value: "exclude",
                  state: "exclude",
                },
                dropdown: "brands",
              }}
            />
          </MantineBox>
        </MantineBox>
      </MantineBox>
    </MantineBox>
  );
};

export default PageContent;
